import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Eye, X, Search, ChevronDown, Check, Users } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

/**
 * CEO-only "View as" trigger in the header. Compact icon + role hint that
 * opens a rich picker: search bar on top, candidates grouped by role, each
 * with region/group context so the CEO instantly knows who is who.
 *
 * Why the previous native <select> got confusing: real APOLLO Prod data has
 * dozens of DNs across every region : a flat list without grouping made it
 * look like a data bug when it was just reality. The new popover surfaces
 * that reality clearly with per-role counts.
 */
const ROLE_ORDER = ["dn", "dr", "dz", "consultant", "president", "coach"];

const ROLE_TONE = {
  dn: { badge: "bg-purple-100 text-purple-800 ring-purple-200", label_fr: "DN", label_en: "ND" },
  dr: { badge: "bg-blue-100 text-blue-800 ring-blue-200", label_fr: "DR", label_en: "RD" },
  dz: { badge: "bg-cyan-100 text-cyan-800 ring-cyan-200", label_fr: "DZ", label_en: "ZD" },
  consultant: { badge: "bg-emerald-100 text-emerald-800 ring-emerald-200", label_fr: "Consultant", label_en: "Consultant" },
  president: { badge: "bg-amber-100 text-amber-800 ring-amber-200", label_fr: "Président", label_en: "President" },
};

export default function ViewAsSwitcher() {
  const { t, i18n } = useTranslation();
  const { realUser, impersonating, user, impersonateAs, stopImpersonating } = useAuth();
  const [candidates, setCandidates] = useState([]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const isEn = (i18n.language || "fr").startsWith("en");

  useEffect(() => {
    if (realUser?.role !== "ceo" || impersonating) return;
    api.get("/admin/impersonate/candidates")
      .then((r) => setCandidates(r.data.candidates || []))
      .catch(() => setCandidates([]));
  }, [realUser, impersonating]);

  const grouped = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q
      ? candidates.filter((c) =>
          [c.full_name, c.email, c.groupe_nom, c.region]
            .filter(Boolean)
            .some((s) => s.toLowerCase().includes(q)),
        )
      : candidates;
    const byRole = {};
    for (const c of filtered) {
      (byRole[c.role] = byRole[c.role] || []).push(c);
    }
    return ROLE_ORDER
      .filter((r) => byRole[r]?.length)
      .map((r) => ({ role: r, items: byRole[r].sort((a, b) => a.full_name.localeCompare(b.full_name)) }));
  }, [candidates, query]);

  if (realUser?.role !== "ceo") return null;

  const total = candidates.length;
  const currentTargetId = impersonating ? user?.id : null;
  const currentTarget = candidates.find((c) => c.id === currentTargetId);

  const pick = (c) => {
    impersonateAs(c);
    setOpen(false);
    setQuery("");
  };

  const back = () => {
    stopImpersonating();
    setOpen(false);
  };

  const roleLabel = (role) => (isEn ? ROLE_TONE[role]?.label_en : ROLE_TONE[role]?.label_fr) || role;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="view-as-trigger"
          className={`inline-flex items-center gap-2 h-9 px-3 rounded-md border text-xs font-medium transition-colors max-w-[200px] ${
            impersonating
              ? "border-apollo-orange bg-apollo-orange-light text-apollo-orange-dark"
              : "border-border bg-white text-text-secondary hover:bg-bg-soft"
          }`}
          title={t("view_as.label")}
        >
          <Eye className="w-3.5 h-3.5 flex-shrink-0" />
          {impersonating && currentTarget ? (
            <span className="truncate font-semibold" data-testid="view-as-current">
              {currentTarget.full_name.split(" ")[0]}
            </span>
          ) : (
            <span className="hidden lg:inline">{t("view_as.label")}</span>
          )}
          <ChevronDown className="w-3.5 h-3.5 opacity-60 flex-shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[380px] p-0" data-testid="view-as-popover">
        <div className="p-3 border-b border-border">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-apollo-orange-dark">
              <Users className="w-3.5 h-3.5" />
              {t("view_as.picker_title", "Voir DELPHI en tant que")}
            </div>
            <span className="text-[10px] text-text-secondary font-mono" data-testid="view-as-total">
              {t("view_as.total_hint", { n: total })}
            </span>
          </div>
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-text-secondary" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("view_as.search_placeholder", "Nom, groupe ou région…")}
              className="w-full h-9 pl-8 pr-3 text-sm rounded-md border border-border bg-white focus:outline-none focus:ring-2 focus:ring-apollo-orange/40"
              data-testid="view-as-search"
              autoFocus
            />
          </div>
        </div>

        <div className="max-h-[420px] overflow-y-auto">
          {impersonating && (
            <button
              type="button"
              onClick={back}
              data-testid="view-as-exit"
              className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left border-b border-border bg-apollo-orange-light/50 hover:bg-apollo-orange-light transition-colors"
            >
              <X className="w-4 h-4 text-apollo-orange" />
              <span className="font-medium text-apollo-orange-dark">{t("view_as.exit")}</span>
            </button>
          )}

          {grouped.length === 0 ? (
            <div className="p-8 text-center text-sm text-text-secondary" data-testid="view-as-empty">
              {t("view_as.no_match", "Aucun résultat")}
            </div>
          ) : (
            grouped.map(({ role, items }) => (
              <div key={role} className="py-1">
                <div className="flex items-center gap-2 px-3 py-1.5 bg-bg-soft/70 sticky top-0">
                  <span className={`inline-block text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ring-1 ${ROLE_TONE[role]?.badge || ""}`}>
                    {roleLabel(role)}
                  </span>
                  <span className="text-[10px] text-text-secondary font-mono">
                    {items.length}
                  </span>
                </div>
                {items.map((c) => {
                  const selected = c.id === currentTargetId;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => pick(c)}
                      data-testid={`view-as-candidate-${c.id}`}
                      className={`w-full flex items-start gap-2 px-3 py-2 text-left hover:bg-bg-soft transition-colors ${selected ? "bg-apollo-orange-light/50" : ""}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-text-primary truncate">{c.full_name}</div>
                        <div className="text-xs text-text-secondary truncate">
                          {c.groupe_nom || c.region || c.email}
                        </div>
                      </div>
                      {selected && <Check className="w-4 h-4 text-apollo-orange flex-shrink-0 mt-0.5" />}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** Persistent orange banner shown at the top of every page while impersonation is active. */
export function ImpersonationBanner() {
  const { t } = useTranslation();
  const { impersonating, user, stopImpersonating } = useAuth();
  if (!impersonating || !user) return null;
  return (
    <div
      className="w-full bg-apollo-orange text-white text-sm px-4 py-2 flex items-center justify-center gap-3"
      data-testid="impersonation-banner"
    >
      <Eye className="w-4 h-4 flex-shrink-0" />
      <span>{t("view_as.banner", { name: user.full_name, role: user.role_label_fr })}</span>
      <button
        onClick={stopImpersonating}
        data-testid="stop-impersonating-btn"
        className="ml-2 inline-flex items-center gap-1 bg-white/20 hover:bg-white/30 rounded px-2 py-0.5 text-xs font-semibold transition-colors"
      >
        <X className="w-3 h-3" />
        {t("view_as.exit")}
      </button>
    </div>
  );
}
